import { useEffect, useMemo, useState, type UIEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, CheckCircle2, CloudCog, Copy, KeyRound, Plus, RefreshCw, RotateCw, Save, Search, ShieldCheck } from 'lucide-react';
import { api } from '../../api';
import { DirectoryFieldSchemasAdministration } from './DirectoryFieldSchemasAdministration';
import './bitrix24-administration.css';

type Bitrix24Settings = {
  enabled: boolean;
  hasOutboundWebhookUrl: boolean;
  inboundWebhookConfigured: boolean;
  companyTypeField: string;
  companyTypeValue: string;
  innField: string;
  kppField: string;
  ogrnField: string;
  legalFormField: string;
  syncIntervalMinutes: number;
  lastTestAt?: string | null;
  lastTestOk?: boolean | null;
  lastTestMessage?: string;
  lastReconcileAt?: string | null;
  lastReconcileOk?: boolean | null;
  lastReconcileMessage?: string;
  lastSuccessAt?: string | null;
  nextReconcileAt?: string | null;
  updatedAt?: string | null;
  queue: { pending: number; failed: number; oldest_pending_at?: string | null };
};

type BitrixQueueItem = {
  id: string;
  entity_type: string;
  external_id: string;
  event_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  safe_error?: string | null;
  created_at: string;
};

type BitrixCompanyCatalogItem = {
  externalId: string;
  name: string;
  companyType?: string | null;
  sourceUpdatedAt?: string | null;
  state: 'AVAILABLE' | 'PENDING' | 'IMPORTED' | 'ERROR';
  portalCompanyId?: string | null;
  lastSyncedAt?: string | null;
};

function dateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function Bitrix24Administration() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({ queryKey: ['bitrix24-settings'], queryFn: () => api<Bitrix24Settings>('/integrations/bitrix24') });
  const { data: failed = [] } = useQuery({ queryKey: ['bitrix24-queue', 'FAILED'], queryFn: () => api<BitrixQueueItem[]>('/integrations/bitrix24/queue?status=FAILED') });
  const { data: companies = [], isLoading: companiesLoading, error: companiesError, refetch: refreshCompanies } = useQuery({
    queryKey: ['bitrix24-companies'],
    queryFn: () => api<BitrixCompanyCatalogItem[]>('/integrations/bitrix24/companies'),
    enabled: Boolean(data?.enabled && data?.hasOutboundWebhookUrl),
  });
  const [form, setForm] = useState({
    enabled: false,
    outboundWebhookUrl: '',
    clearOutboundWebhookUrl: false,
    companyTypeField: 'COMPANY_TYPE',
    companyTypeValue: 'CUSTOMER',
    innField: 'UF_CRM_INN',
    kppField: 'UF_CRM_KPP',
    ogrnField: 'UF_CRM_OGRN',
    legalFormField: 'UF_CRM_LEGAL_FORM',
    syncIntervalMinutes: 60,
  });
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(() => new Set());
  const [visibleCompanies, setVisibleCompanies] = useState(50);

  const filteredCompanies = useMemo(() => {
    const needle = companySearch.trim().toLocaleLowerCase('ru');
    if (!needle) return companies;
    return companies.filter(company => company.name.toLocaleLowerCase('ru').includes(needle) || company.externalId.includes(needle));
  }, [companies, companySearch]);
  const catalogRows = filteredCompanies.slice(0, visibleCompanies);
  const availableCompanies = filteredCompanies.filter(company => company.state === 'AVAILABLE');

  useEffect(() => setVisibleCompanies(50), [companySearch, companies.length]);

  useEffect(() => {
    if (!data) return;
    setForm(value => ({
      ...value,
      enabled: data.enabled,
      companyTypeField: data.companyTypeField,
      companyTypeValue: data.companyTypeValue,
      innField: data.innField,
      kppField: data.kppField,
      ogrnField: data.ogrnField,
      legalFormField: data.legalFormField,
      syncIntervalMinutes: data.syncIntervalMinutes,
      outboundWebhookUrl: '',
      clearOutboundWebhookUrl: false,
    }));
  }, [data]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['bitrix24-settings'] }),
      queryClient.invalidateQueries({ queryKey: ['bitrix24-queue'] }),
      queryClient.invalidateQueries({ queryKey: ['bitrix24-companies'] }),
      queryClient.invalidateQueries({ queryKey: ['monitoring'] }),
    ]);
  };

  const save = useMutation({
    mutationFn: () => api<Bitrix24Settings>('/integrations/bitrix24', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: async () => { setError(''); setFeedback('Настройки Битрикс24 сохранены'); await refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки'),
  });
  const test = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>('/integrations/bitrix24/test', { method: 'POST' }),
    onSuccess: async result => { setError(''); setFeedback(result.message); await refresh(); },
    onError: reason => { setFeedback(''); setError(reason instanceof Error ? reason.message : 'Битрикс24 недоступен'); void refresh(); },
  });
  const reconcile = useMutation({
    mutationFn: () => api<{ discovered: number; queued: number; archived: number }>('/integrations/bitrix24/reconcile', { method: 'POST' }),
    onSuccess: async result => { setError(''); setFeedback(`Сверка выполнена: найдено ${result.discovered}, в очереди ${result.queued}, архивировано ${result.archived}.`); await refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Сверка не выполнена'),
  });
  const rotate = useMutation({
    mutationFn: () => api<{ webhookUrl: string; shownOnce: boolean }>('/integrations/bitrix24/rotate-webhook-secret', { method: 'POST', body: JSON.stringify({ origin: window.location.origin }) }),
    onSuccess: async result => { setError(''); setWebhookUrl(result.webhookUrl); setFeedback('Новый адрес входящего webhook создан. Скопируйте его сейчас.'); await refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось создать webhook'),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api(`/integrations/bitrix24/queue/${id}/retry`, { method: 'POST' }),
    onSuccess: async () => { setError(''); setFeedback('Ошибка возвращена в очередь повторов'); await refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось повторить синхронизацию'),
  });
  const importCompanies = useMutation({
    mutationFn: (externalIds: string[]) => api<{ selected: number; queued: number }>('/integrations/bitrix24/companies/import', {
      method: 'POST',
      body: JSON.stringify({ externalIds }),
    }),
    onSuccess: async result => {
      setError('');
      setFeedback(`Компании добавлены: ${result.selected}. Синхронизация поставлена в очередь: ${result.queued}.`);
      setSelectedCompanies(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bitrix24-companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['bitrix24-settings'] }),
      ]);
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось добавить компании'),
  });

  function toggleCompany(externalId: string) {
    setSelectedCompanies(current => {
      const next = new Set(current);
      if (next.has(externalId)) next.delete(externalId);
      else next.add(externalId);
      return next;
    });
  }

  function loadMoreCompanies(event: UIEvent<HTMLDivElement>) {
    if (visibleCompanies >= filteredCompanies.length) return;
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight > 100) return;
    setVisibleCompanies(current => Math.min(current + 50, filteredCompanies.length));
  }

  if (isLoading) return <div className="surface bitrix-loading">Загрузка настроек Битрикс24…</div>;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить настройки Битрикс24'}</div>;

  return <div className="bitrix-admin">
    <section className="surface bitrix-admin__hero">
      <div className="bitrix-admin__logo">Б24</div>
      <div><h2>Битрикс24</h2><p>Источник юридических и контактных данных клиентов</p></div>
      <span className={`bitrix-admin__state ${data.enabled && data.lastTestOk ? 'connected' : ''}`}>
        {data.enabled && data.lastTestOk ? <CheckCircle2 /> : <AlertTriangle />}
        {data.enabled && data.lastTestOk ? 'Подключено' : data.enabled ? 'Требует проверки' : 'Выключено'}
      </span>
    </section>

    {(feedback || error) && <div className={`admin-feedback ${error ? 'error' : 'success'}`}>{error || feedback}</div>}

    <section className="surface bitrix-admin__section">
      <div className="bitrix-admin__section-head"><div><h3>Подключение и синхронизация</h3><p>Только компании типа «Клиенты» и связанные с ними контакты</p></div><CloudCog /></div>
      <label className="switch-setting"><span><b>Синхронизация включена</b><small>Webhook обрабатывается сразу, полная сверка выполняется по расписанию.</small></span><input type="checkbox" checked={form.enabled} onChange={event => setForm(value => ({ ...value, enabled: event.target.checked }))} /></label>
      <div className="bitrix-admin__grid">
        <label className="wide">Исходящий webhook Битрикс24<input type="password" value={form.outboundWebhookUrl} onChange={event => setForm(value => ({ ...value, outboundWebhookUrl: event.target.value, clearOutboundWebhookUrl: false }))} placeholder={data.hasOutboundWebhookUrl ? 'Webhook сохранён. Введите новый только для замены.' : 'https://ваш-портал.bitrix24.ru/rest/пользователь/секрет'} autoComplete="new-password" /></label>
        <label>Интервал полной сверки, минут<input type="number" min={5} max={10080} value={form.syncIntervalMinutes} onChange={event => setForm(value => ({ ...value, syncIntervalMinutes: Number(event.target.value) }))} /></label>
        {data.hasOutboundWebhookUrl && <label className="switch-setting compact"><span><b>Удалить webhook</b><small>Обмен остановится после сохранения.</small></span><input type="checkbox" checked={form.clearOutboundWebhookUrl} onChange={event => setForm(value => ({ ...value, clearOutboundWebhookUrl: event.target.checked, outboundWebhookUrl: '' }))} /></label>}
      </div>
      <div className="bitrix-admin__actions">
        <button className="button primary" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить'}</button>
        <button className="button ghost" onClick={() => test.mutate()} disabled={test.isPending || !data.hasOutboundWebhookUrl}><ShieldCheck /> {test.isPending ? 'Проверка…' : 'Проверить подключение'}</button>
        <button className="button ghost" onClick={() => reconcile.mutate()} disabled={reconcile.isPending || !data.enabled}><RefreshCw /> {reconcile.isPending ? 'Сверка…' : 'Сверить сейчас'}</button>
      </div>
    </section>

    <div className="bitrix-admin__status-grid">
      <section className="surface"><small>Последнее подключение</small><b>{data.lastTestMessage || 'Не проверялось'}</b><span>{dateTime(data.lastTestAt)}</span></section>
      <section className="surface"><small>Последняя сверка</small><b>{data.lastReconcileMessage || 'Ещё не выполнялась'}</b><span>{dateTime(data.lastReconcileAt)}</span></section>
      <section className="surface"><small>Следующая сверка</small><b>{dateTime(data.nextReconcileAt)}</b><span>Успешный обмен: {dateTime(data.lastSuccessAt)}</span></section>
      <section className={`surface ${data.queue.failed ? 'attention' : ''}`}><small>Очередь</small><b>{data.queue.pending} в работе · {data.queue.failed} ошибок</b><span>Повторы выполняются автоматически</span></section>
    </div>

    <section className="surface bitrix-admin__section bitrix-company-catalog">
      <div className="bitrix-admin__section-head bitrix-company-catalog__head">
        <div><h3>Компании Битрикс24</h3><p>Выберите клиентов, которых нужно добавить и автоматически обновлять на портале</p></div>
        <Building2 />
      </div>
      <div className="bitrix-company-catalog__toolbar">
        <label className="bitrix-company-catalog__search"><Search /><input aria-label="Поиск компаний Битрикс24" value={companySearch} onChange={event => setCompanySearch(event.target.value)} placeholder="Название или ID в Битрикс24" /></label>
        <span className="bitrix-company-catalog__summary">{companies.filter(company => company.state === 'IMPORTED').length} добавлено · {companies.filter(company => company.state === 'AVAILABLE').length} доступно</span>
        <button className="button ghost" type="button" onClick={() => void refreshCompanies()} disabled={companiesLoading}><RefreshCw /> {companiesLoading ? 'Обновление…' : 'Обновить список'}</button>
        <button className="button primary" type="button" disabled={!selectedCompanies.size || importCompanies.isPending} onClick={() => importCompanies.mutate([...selectedCompanies])}><Plus /> {importCompanies.isPending ? 'Добавление…' : `Добавить выбранные · ${selectedCompanies.size}`}</button>
      </div>
      {companiesError && <div className="admin-feedback error">{companiesError instanceof Error ? companiesError.message : 'Не удалось загрузить компании Битрикс24'}</div>}
      {!companiesError && <div className="bitrix-company-catalog__list" onScroll={loadMoreCompanies}>
        <div className="bitrix-company-catalog__columns"><span>Выбор</span><span>Компания</span><span>Обновление</span><span>Состояние</span></div>
        {catalogRows.map(company => {
          const available = company.state === 'AVAILABLE';
          const status = company.state === 'IMPORTED' ? 'Добавлена' : company.state === 'PENDING' ? 'Добавляется' : company.state === 'ERROR' ? 'Ошибка' : 'Можно добавить';
          return <label className={`bitrix-company-catalog__row state-${company.state.toLowerCase()}`} key={company.externalId}>
            <span className="bitrix-company-catalog__check">{available ? <input type="checkbox" aria-label={`Выбрать ${company.name}`} checked={selectedCompanies.has(company.externalId)} onChange={() => toggleCompany(company.externalId)} /> : <CheckCircle2 />}</span>
            <span className="bitrix-company-catalog__identity"><b>{company.name}</b><small>ID {company.externalId}{company.companyType ? ` · ${company.companyType}` : ''}</small></span>
            <span className="bitrix-company-catalog__updated">{company.lastSyncedAt ? dateTime(company.lastSyncedAt) : company.sourceUpdatedAt ? dateTime(company.sourceUpdatedAt) : '—'}</span>
            <span className={`bitrix-company-catalog__badge ${company.state.toLowerCase()}`}>{status}</span>
          </label>;
        })}
        {companiesLoading && !companies.length && <div className="bitrix-company-catalog__empty">Получаем компании из Битрикс24…</div>}
        {!companiesLoading && !catalogRows.length && <div className="bitrix-company-catalog__empty">{companySearch ? 'По запросу компании не найдены' : 'Компании типа «Клиент» не найдены'}</div>}
        {catalogRows.length < filteredCompanies.length && <div className="bitrix-company-catalog__more">Показано {catalogRows.length} из {filteredCompanies.length} · прокрутите ниже</div>}
      </div>}
      {availableCompanies.length > 0 && <button className="bitrix-company-catalog__select-all" type="button" onClick={() => setSelectedCompanies(new Set(availableCompanies.map(company => company.externalId)))}>Выбрать все доступные ({availableCompanies.length})</button>}
    </section>

    <section className="surface bitrix-admin__section">
      <div className="bitrix-admin__section-head"><div><h3>Входящий webhook</h3><p>Адрес подтверждается отдельным секретом и не показывается повторно</p></div><KeyRound /></div>
      <div className="bitrix-admin__actions"><button className="button ghost" onClick={() => rotate.mutate()} disabled={rotate.isPending}><RotateCw /> {data.inboundWebhookConfigured ? 'Перевыпустить адрес' : 'Создать адрес'}</button></div>
      {webhookUrl && <div className="bitrix-admin__webhook"><code>{webhookUrl}</code><button className="icon-button" title="Скопировать" onClick={() => void navigator.clipboard?.writeText(webhookUrl)}><Copy /></button></div>}
      {!webhookUrl && data.inboundWebhookConfigured && <p className="bitrix-admin__safe-note">Адрес уже настроен. Полный секрет скрыт; при необходимости выпустите новый.</p>}
    </section>

    <button className="bitrix-admin__advanced-toggle" onClick={() => setAdvanced(value => !value)}>{advanced ? 'Скрыть сопоставление полей' : 'Показать сопоставление полей'}</button>
    {advanced && <section className="surface bitrix-admin__section">
      <div className="bitrix-admin__section-head"><div><h3>Сопоставление полей</h3><p>Стандартные реквизиты Битрикс24 читаются автоматически. Коды ниже используются только как резервные пользовательские поля.</p></div></div>
      <div className="bitrix-admin__grid mapping">
        <label>Поле типа компании<input value={form.companyTypeField} onChange={event => setForm(value => ({ ...value, companyTypeField: event.target.value }))} /></label>
        <label>Значение «Клиенты»<input value={form.companyTypeValue} onChange={event => setForm(value => ({ ...value, companyTypeValue: event.target.value }))} /></label>
        <label>ИНН<input value={form.innField} onChange={event => setForm(value => ({ ...value, innField: event.target.value }))} /></label>
        <label>КПП<input value={form.kppField} onChange={event => setForm(value => ({ ...value, kppField: event.target.value }))} /></label>
        <label>ОГРН<input value={form.ogrnField} onChange={event => setForm(value => ({ ...value, ogrnField: event.target.value }))} /></label>
        <label>Юридическая форма<input value={form.legalFormField} onChange={event => setForm(value => ({ ...value, legalFormField: event.target.value }))} /></label>
      </div>
    </section>}

    <DirectoryFieldSchemasAdministration />

    {failed.length > 0 && <section className="surface bitrix-admin__section">
      <div className="bitrix-admin__section-head"><div><h3>Ошибки синхронизации</h3><p>Показаны безопасные описания без токенов и персональных payload</p></div><AlertTriangle /></div>
      <div className="bitrix-admin__queue">{failed.map(item => <div key={item.id}><span><b>{item.entity_type === 'COMPANY' ? 'Компания' : 'Контакт'} · ID {item.external_id}</b><small>{item.safe_error || 'Причина не указана'} · попыток {item.attempts}/{item.max_attempts} · {dateTime(item.created_at)}</small></span><button className="button ghost" onClick={() => retry.mutate(item.id)} disabled={retry.isPending}><RotateCw /> Повторить</button></div>)}</div>
    </section>}
  </div>;
}
